<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
  onBeforeUnmount,
} from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import L from 'leaflet';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import { storeLocationsApi } from 'dashboard/api/locations';

const props = defineProps({
  show: { type: Boolean, default: false },
  type: { type: String, default: 'store' }, // 'store' or 'zone'
  mode: { type: String, default: 'create' }, // 'create' or 'edit'
  locationData: { type: Object, default: () => ({}) },
  stores: { type: Array, default: () => [] },
});
const emit = defineEmits(['close', 'submit']);
const { t } = useI18n();
const addressNeedsSearch = ref(false);
const storeSearchQuery = ref('');
const showStoreSuggestions = ref(false);
const storeSearchResults = ref([]);
const isSearchingStores = ref(false);
let storeSearchTimeout = null;

// Fix leaflet icon paths
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Leaflet exposes these internal members as part of its documented map setup.
/* eslint-disable no-underscore-dangle */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

onMounted(() => {
  if (!document.getElementById('leaflet-css')) {
    const link = document.createElement('link');
    link.id = 'leaflet-css';
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }
});

const isLoading = ref(false);
const formData = ref({
  name: '',
  address: '',
  zip_code: '',
  phone: '',
  latitude: -23.5505, // SP default
  longitude: -46.6333,
  active: true,
  pricing_model: 'fixed',
  base_fee: 10.0,
  fee_per_km: 0.0,
  shape_type: 'circle',
  start_angle: 0,
  end_angle: 90,
  polygon_points: [],
});

let map = null;
let marker = null;
let shapeLayer = null;
let storeShapeLayer = null;
let resizeObserver = null;

const selectedStore = ref(null);

const maxAllowedRadius = computed(() => {
  if (props.type === 'zone' && selectedStore.value) {
    return (parseFloat(selectedStore.value.max_radius_km) || 5) * 1000;
  }
  return undefined;
});

const onStoreSearchInput = () => {
  if (storeSearchTimeout) clearTimeout(storeSearchTimeout);

  if (!storeSearchQuery.value || storeSearchQuery.value.trim().length < 2) {
    storeSearchResults.value = [];
    return;
  }

  isSearchingStores.value = true;
  storeSearchTimeout = setTimeout(async () => {
    try {
      const response = await storeLocationsApi.get({
        search: storeSearchQuery.value,
      });
      storeSearchResults.value = response.data.payload || [];
    } catch (e) {
      storeSearchResults.value = [];
    } finally {
      isSearchingStores.value = false;
    }
  }, 400);
};

const onStoreSearchBlur = () => {
  setTimeout(() => {
    showStoreSuggestions.value = false;
  }, 200);
};

const onStoreSelect = store => {
  if (store) {
    selectedStore.value = store;
    formData.value.store_location_id = store.id;
    storeSearchQuery.value = store.name;
    formData.value.latitude = store.latitude || -23.5505;
    formData.value.longitude = store.longitude || -46.6333;
    showStoreSuggestions.value = false;
    if (map) {
      map.setView(
        [
          parseFloat(store.latitude) || -23.5505,
          parseFloat(store.longitude) || -46.6333,
        ],
        13
      );
    }
  }
};

const title = computed(() => {
  const isStore = props.type === 'store';
  if (props.mode === 'create')
    return isStore ? t('LOCATIONS.NEW_STORE') : t('LOCATIONS.NEW_ZONE');
  return isStore ? t('LOCATIONS.EDIT_STORE') : t('LOCATIONS.EDIT_ZONE');
});

const subtitle = computed(() => {
  return props.type === 'store'
    ? t('LOCATIONS.STORE_SUBTITLE')
    : t('LOCATIONS.ZONE_SUBTITLE');
});

const mapContainerElement = ref(null);

const initMap = async (retryCount = 0) => {
  try {
    const container = mapContainerElement.value;
    if (!container) {
      if (retryCount < 10) {
        setTimeout(() => initMap(retryCount + 1), 50);
      }
      return;
    }

    if (container._leaflet_id) {
      container._leaflet_id = null;
    }

    if (map) {
      map.remove();
      map = null;
    }

    const rawLat = formData.value.latitude;
    const rawLng = formData.value.longitude;
    const lat = parseFloat(rawLat?.toString().replace(',', '.')) || -23.5505;
    const lng = parseFloat(rawLng?.toString().replace(',', '.')) || -46.6333;

    map = L.map(container, {
      scrollWheelZoom: false,
    }).setView([lat, lng], 13);

    const handleWheel = e => {
      e.preventDefault();
      if (!map) return;

      const now = Date.now();
      if (container._lastZoomTime && now - container._lastZoomTime < 400) {
        return;
      }

      if (!container._wheelDelta) container._wheelDelta = 0;
      container._wheelDelta += e.deltaY;

      if (Math.abs(container._wheelDelta) > 50) {
        if (container._wheelDelta > 0) map.setZoom(map.getZoom() - 1);
        else map.setZoom(map.getZoom() + 1);

        container._wheelDelta = 0;
        container._lastZoomTime = now;
      }

      clearTimeout(container._wheelTimer);
      container._wheelTimer = setTimeout(() => {
        container._wheelDelta = 0;
      }, 100);
    };

    if (container._wheelListener) {
      container.removeEventListener('wheel', container._wheelListener);
    }
    container._wheelListener = handleWheel;
    container.addEventListener('wheel', handleWheel, { passive: false });
    /* eslint-enable no-underscore-dangle */

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    // The map update function is declared below the Leaflet initialization block.
    // eslint-disable-next-line no-use-before-define
    updateMap();

    map.on('click', e => {
      if (formData.value.shape_type === 'polygon') {
        if (!formData.value.polygon_points) formData.value.polygon_points = [];
        formData.value.polygon_points.push({
          lat: e.latlng.lat,
          lng: e.latlng.lng,
        });
      } else {
        if (props.type === 'zone') return; // Do not allow changing lat/lng for zones
        formData.value.latitude = e.latlng.lat.toFixed(6);
        formData.value.longitude = e.latlng.lng.toFixed(6);
      }
    });

    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    resizeObserver = new ResizeObserver(() => {
      if (map) map.invalidateSize();
    });
    resizeObserver.observe(container);

    setTimeout(() => {
      if (map) map.invalidateSize();
    }, 350);
  } catch (error) {
    useAlert(t('LOCATIONS.ERRORS.MAP', { error: error.message }));
  }
};

const isSearchingAddress = ref(false);
const addressSuggestions = ref([]);
const showSuggestions = ref(false);
let searchTimeout = null;

const getSuggestionTitle = suggestion => {
  const parts = suggestion.display_name.split(',');
  return parts[0].trim();
};

const getSuggestionSubtitle = suggestion => {
  const parts = suggestion.display_name.split(',');
  return parts.slice(1).join(',').trim();
};

const onAddressInput = () => {
  addressNeedsSearch.value = true;
  showSuggestions.value = true;
  clearTimeout(searchTimeout);
  if (formData.value.address.length < 4) {
    addressSuggestions.value = [];
    return;
  }

  searchTimeout = setTimeout(async () => {
    addressSuggestions.value = [];
    try {
      const query = encodeURIComponent(formData.value.address);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${query}&limit=5`
      );
      const data = await response.json();
      addressSuggestions.value = data || [];
    } catch (e) {
      addressSuggestions.value = [];
    }
  }, 500);
};

const onAddressBlur = () => {
  setTimeout(() => {
    showSuggestions.value = false;
  }, 200);
};

const selectSuggestion = suggestion => {
  const typedAddress = formData.value.address || '';
  const matchNum = typedAddress.match(/(?:,\s*|\s+)(\d+[A-Za-z]?)\b/);

  let finalAddress = suggestion.display_name;
  if (matchNum && !suggestion.address?.house_number) {
    const num = matchNum[1];
    const parts = suggestion.display_name.split(',');
    if (parts.length > 1) {
      finalAddress = `${parts[0].trim()}, ${num} - ${parts.slice(1).join(',').trim()}`;
    }
  }

  formData.value.address = finalAddress;
  formData.value.latitude = parseFloat(suggestion.lat);
  formData.value.longitude = parseFloat(suggestion.lon);

  if (map) {
    map.setView([parseFloat(suggestion.lat), parseFloat(suggestion.lon)], 13);
  }

  if (suggestion.address && suggestion.address.postcode) {
    const rawZip = suggestion.address.postcode.replace(/\D/g, '');
    formData.value.zip_code = rawZip.replace(
      /^(\d{5})(\d{3})?$/,
      (match, p1, p2) => (p2 ? `${p1}-${p2}` : p1)
    );
  }

  showSuggestions.value = false;
  addressSuggestions.value = [];
  addressNeedsSearch.value = false;
  useAlert(t('LOCATIONS.SUCCESS.LOCATION_UPDATED'));
};

const onZipCodeInput = event => {
  let value = event.target.value.replace(/\D/g, '');
  if (value.length > 8) value = value.slice(0, 8);
  if (value.length > 5) {
    value = value.replace(/^(\d{5})(\d)/, '$1-$2');
  }
  formData.value.zip_code = value;
  event.target.value = value;
};

const onPhoneInput = event => {
  let value = event.target.value.replace(/\D/g, '');
  if (value.length > 11) value = value.slice(0, 11);

  if (value.length > 10) {
    value = value.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (value.length > 6) {
    value = value.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  } else if (value.length > 2) {
    value = value.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
  } else if (value.length > 0) {
    value = value.replace(/^(\d*)/, '($1');
  }

  formData.value.phone = value;
  event.target.value = value;
};

const searchAddress = async () => {
  if (!formData.value.address) {
    useAlert(t('LOCATIONS.ERRORS.ADDRESS_REQUIRED'));
    return;
  }

  isSearchingAddress.value = true;
  try {
    const query = encodeURIComponent(formData.value.address);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${query}&limit=1`
    );
    const data = await response.json();

    if (data && data.length > 0) {
      selectSuggestion(data[0]);
    } else {
      useAlert(t('LOCATIONS.ERRORS.ADDRESS_NOT_FOUND'));
    }
  } catch (error) {
    useAlert(t('LOCATIONS.ERRORS.ADDRESS_SEARCH'));
  } finally {
    isSearchingAddress.value = false;
  }
};

const clearPolygon = () => {
  formData.value.polygon_points = [];
  // eslint-disable-next-line no-use-before-define
  updateMap();
};

const generateSectorPoints = (center, radiusMetres, startAngle, endAngle) => {
  const points = [center];
  const earthRadius = 6371000;
  let start = parseFloat(startAngle) || 0;
  let end = parseFloat(endAngle) || 0;
  if (start > end) end += 360;

  const step = 2;
  const numSteps = Math.max(1, Math.ceil((end - start) / step));

  for (let i = 0; i <= numSteps; i += 1) {
    const angle = start + (i * (end - start)) / numSteps;
    const brng = (angle * Math.PI) / 180;
    const lat1 = (center[0] * Math.PI) / 180;
    const lon1 = (center[1] * Math.PI) / 180;
    const distRad = radiusMetres / earthRadius;

    // Calculate angular distance and clamp to [-1, 1] to prevent NaN due to floating point inaccuracies
    let angularDist =
      Math.sin(lat1) * Math.cos(distRad) +
      Math.cos(lat1) * Math.sin(distRad) * Math.cos(brng);
    angularDist = Math.max(-1, Math.min(1, angularDist));

    const lat2 = Math.asin(angularDist);
    const lon2 =
      lon1 +
      Math.atan2(
        Math.sin(brng) * Math.sin(distRad) * Math.cos(lat1),
        Math.cos(distRad) - Math.sin(lat1) * Math.sin(lat2)
      );

    if (!Number.isNaN(lat2) && !Number.isNaN(lon2)) {
      points.push([(lat2 * 180) / Math.PI, (lon2 * 180) / Math.PI]);
    }
  }
  return points;
};

function updateMap() {
  if (!map) return;

  const lat = parseFloat(formData.value.latitude) || -23.5505;
  const lng = parseFloat(formData.value.longitude) || -46.6333;
  const newLatLng = new L.LatLng(lat, lng);

  if (formData.value.shape_type !== 'polygon') {
    if (marker) {
      marker.setLatLng(newLatLng);
    } else {
      marker = L.marker(newLatLng, { draggable: props.type !== 'zone' }).addTo(
        map
      );
      marker.on('dragend', e => {
        if (props.type === 'zone') return;
        const pos = e.target.getLatLng();
        formData.value.latitude = pos.lat.toFixed(6);
        formData.value.longitude = pos.lng.toFixed(6);
      });
    }
  } else if (marker) {
    map.removeLayer(marker);
    marker = null;
  }

  if (shapeLayer) {
    map.removeLayer(shapeLayer);
    shapeLayer = null;
  }

  if (storeShapeLayer) {
    map.removeLayer(storeShapeLayer);
    storeShapeLayer = null;
  }

  // Draw store context if zone
  if (props.type === 'zone' && selectedStore.value) {
    const sColor = '#2563EB'; // Blue
    const sFillColor = '#3B82F6';
    const sLat = parseFloat(selectedStore.value.latitude) || lat;
    const sLng = parseFloat(selectedStore.value.longitude) || lng;
    const sRadius = (parseFloat(selectedStore.value.max_radius_km) || 5) * 1000; // default 5km
    const sShape = selectedStore.value.shape_type || 'circle';

    if (sShape === 'circle') {
      storeShapeLayer = L.circle([sLat, sLng], {
        color: sColor,
        fillColor: sFillColor,
        fillOpacity: 0.05,
        dashArray: '5, 5',
        radius: sRadius,
      }).addTo(map);
    } else if (sShape === 'sector') {
      const pts = generateSectorPoints(
        [sLat, sLng],
        sRadius,
        selectedStore.value.start_angle || 0,
        selectedStore.value.end_angle || 90
      );
      storeShapeLayer = L.polygon(pts, {
        color: sColor,
        fillColor: sFillColor,
        fillOpacity: 0.05,
        dashArray: '5, 5',
      }).addTo(map);
    } else if (
      sShape === 'polygon' &&
      selectedStore.value.polygon_points &&
      selectedStore.value.polygon_points.length > 0
    ) {
      const pts = selectedStore.value.polygon_points
        .map(p => [parseFloat(p.lat), parseFloat(p.lng)])
        .filter(p => !Number.isNaN(p[0]) && !Number.isNaN(p[1]));
      if (pts.length > 0) {
        storeShapeLayer = L.polygon(pts, {
          color: sColor,
          fillColor: sFillColor,
          fillOpacity: 0.05,
          dashArray: '5, 5',
        }).addTo(map);
      }
    }
  }

  const color = props.type === 'zone' ? '#DC2626' : '#2563EB';
  const fillColor = props.type === 'zone' ? '#EF4444' : '#3B82F6';

  if (formData.value.shape_type === 'circle') {
    shapeLayer = L.circle(newLatLng, {
      color: color,
      fillColor: fillColor,
      fillOpacity: 0.1,
      radius: parseFloat(formData.value.radius) || 5000,
    }).addTo(map);
  } else if (formData.value.shape_type === 'sector') {
    const pts = generateSectorPoints(
      [lat, lng],
      parseFloat(formData.value.radius) || 5000,
      formData.value.start_angle,
      formData.value.end_angle
    );
    shapeLayer = L.polygon(pts, {
      color: color,
      fillColor: fillColor,
      fillOpacity: 0.1,
    }).addTo(map);
  } else if (
    formData.value.shape_type === 'polygon' &&
    formData.value.polygon_points &&
    formData.value.polygon_points.length > 0
  ) {
    const pts = formData.value.polygon_points
      .map(p => [parseFloat(p.lat), parseFloat(p.lng)])
      .filter(p => !Number.isNaN(p[0]) && !Number.isNaN(p[1]));
    if (pts.length > 0) {
      shapeLayer = L.polygon(pts, {
        color: color,
        fillColor: fillColor,
        fillOpacity: 0.1,
      }).addTo(map);
    }
  }
}

const getInitialRadius = () => {
  const source =
    props.type === 'store'
      ? props.locationData.max_radius_km
      : props.locationData.radius_km;
  const radius = parseFloat(source);
  return Number.isNaN(radius) ? 5000 : radius * 1000;
};

watch(() => formData.value.latitude, updateMap);
watch(() => formData.value.longitude, updateMap);
watch(
  () => formData.value.radius,
  newVal => {
    const max = maxAllowedRadius.value;
    if (max !== undefined && newVal > max) {
      formData.value.radius = max;
    }
    updateMap();
  }
);
watch(() => formData.value.shape_type, updateMap);
watch(() => formData.value.start_angle, updateMap);
watch(() => formData.value.end_angle, updateMap);
watch(() => formData.value.polygon_points, updateMap, { deep: true });

watch(
  () => props.show,
  async newVal => {
    if (newVal) {
      formData.value = {
        phone: '',
        latitude: -23.5505,
        longitude: -46.6333,
        active: true,
        pricing_model: 'fixed',
        base_fee: 10.0,
        shape_type: 'circle',
        start_angle: 0,
        end_angle: 90,
        polygon_points: [],
        ...props.locationData,
        radius: getInitialRadius(),
      };
      if (props.type === 'zone') {
        selectedStore.value = props.stores.find(
          s =>
            s.id === formData.value.store_location_id ||
            (!formData.value.store_location_id &&
              Math.abs(
                parseFloat(s.latitude) - parseFloat(formData.value.latitude)
              ) < 0.0001 &&
              Math.abs(
                parseFloat(s.longitude) - parseFloat(formData.value.longitude)
              ) < 0.0001)
        );
        if (selectedStore.value) {
          storeSearchQuery.value = selectedStore.value.name;
        }
      }
      await nextTick();
      initMap();
    } else {
      if (map) {
        map.remove();
        map = null;
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (map) {
    map.remove();
    map = null;
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

const closeModal = () => emit('close');

const submitForm = async () => {
  if (
    formData.value.shape_type === 'polygon' &&
    (!formData.value.polygon_points || formData.value.polygon_points.length < 3)
  ) {
    useAlert(t('LOCATIONS.ERRORS.POLYGON_POINTS'));
    return;
  }

  if (
    props.type === 'store' &&
    (!formData.value.address || formData.value.address.trim().length < 8)
  ) {
    useAlert(t('LOCATIONS.ERRORS.VALID_ADDRESS'));
    return;
  }

  if (
    props.type === 'zone' &&
    (!storeSearchQuery.value || storeSearchQuery.value.trim().length < 2)
  ) {
    useAlert(t('LOCATIONS.ERRORS.VALID_STORE'));
    return;
  }

  if (addressNeedsSearch.value && props.type === 'store') {
    await searchAddress();
  }

  // Ensure the backend receives the correct radius field (in km)
  const finalData = { ...formData.value };
  const radiusKm = (parseFloat(finalData.radius) || 5000) / 1000.0;

  if (props.type === 'store') {
    finalData.max_radius_km = radiusKm;
  } else {
    finalData.radius_km = radiusKm;
  }

  emit('submit', finalData);
};
</script>

<template>
  <woot-modal :show="show" size="large" @close="closeModal">
    <div
      class="flex flex-col h-auto overflow-auto w-full p-4 location-modal-container"
    >
      <woot-modal-header :header-title="title" :header-content="subtitle" />
      <form
        class="flex flex-col w-full gap-4 mt-4"
        @submit.prevent="submitForm"
      >
        <div class="flex flex-row gap-4 w-full">
          <!-- Left side: Form fields -->
          <div class="flex flex-col gap-2 w-1/2">
            <div class="w-full">
              <label>
                {{ $t('LOCATIONS.NAME') }}
                <input
                  v-model="formData.name"
                  type="text"
                  :placeholder="$t('LOCATIONS.NAME_PLACEHOLDER')"
                  required
                />
              </label>
            </div>

            <template v-if="type === 'store'">
              <div class="w-full">
                <label class="block mb-1">{{ $t('LOCATIONS.ADDRESS') }}</label>
                <div class="flex gap-2 relative">
                  <div class="w-full relative">
                    <input
                      v-model="formData.address"
                      class="w-full"
                      type="text"
                      :placeholder="$t('LOCATIONS.ADDRESS_PLACEHOLDER')"
                      @input="onAddressInput"
                      @focus="showSuggestions = true"
                      @blur="onAddressBlur"
                    />
                    <div
                      v-if="showSuggestions && addressSuggestions.length > 0"
                      class="absolute z-[1000] w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto"
                    >
                      <div
                        v-for="(suggestion, index) in addressSuggestions"
                        :key="index"
                        class="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer border-b border-slate-100 dark:border-slate-800 last:border-0"
                        @mousedown.prevent="selectSuggestion(suggestion)"
                      >
                        <div
                          class="font-medium text-sm text-slate-800 dark:text-slate-100"
                        >
                          {{ getSuggestionTitle(suggestion) }}
                        </div>
                        <div class="text-xs text-slate-500 truncate">
                          {{ getSuggestionSubtitle(suggestion) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <NextButton
                    faded
                    slate
                    icon="i-lucide-search"
                    :is-loading="isSearchingAddress"
                    :title="$t('LOCATIONS.SEARCH_MAP')"
                    @click.prevent="searchAddress"
                  />
                </div>
              </div>
              <div class="flex gap-4">
                <div class="w-1/2">
                  <label>
                    {{ $t('LOCATIONS.ZIP_CODE') }}
                    <input
                      :value="formData.zip_code"
                      type="text"
                      :placeholder="$t('LOCATIONS.ZIP_PLACEHOLDER')"
                      maxlength="9"
                      @input="onZipCodeInput"
                    />
                  </label>
                </div>
                <div class="w-1/2">
                  <label>
                    {{ $t('LOCATIONS.PHONE_LABEL') }}
                    <input
                      :value="formData.phone"
                      type="text"
                      :placeholder="$t('LOCATIONS.PHONE_PLACEHOLDER')"
                      maxlength="15"
                      @input="onPhoneInput"
                    />
                  </label>
                </div>
              </div>
              <div class="flex gap-4">
                <div
                  :class="
                    formData.pricing_model === 'dynamic' ? 'w-1/3' : 'w-1/2'
                  "
                >
                  <label>
                    {{ $t('LOCATIONS.PRICING_MODEL') }}
                    <select v-model="formData.pricing_model">
                      <option value="fixed">
                        {{ $t('LOCATIONS.FIXED_FEE') }}
                      </option>
                      <option value="dynamic">
                        {{ $t('LOCATIONS.DYNAMIC_FEE') }}
                      </option>
                      <option value="distance_table">
                        {{ $t('LOCATIONS.DISTANCE_TABLE') }}
                      </option>
                    </select>
                  </label>
                </div>
                <div
                  :class="
                    formData.pricing_model === 'dynamic' ? 'w-1/3' : 'w-1/2'
                  "
                >
                  <label>
                    {{ $t('LOCATIONS.BASE_FEE') }}
                    <input
                      v-model.number="formData.base_fee"
                      type="number"
                      step="0.01"
                    />
                  </label>
                </div>
                <div v-if="formData.pricing_model === 'dynamic'" class="w-1/3">
                  <label>
                    {{ $t('LOCATIONS.FEE_PER_KM') }}
                    <input
                      v-model.number="formData.fee_per_km"
                      type="number"
                      step="0.01"
                    />
                  </label>
                </div>
              </div>
            </template>

            <template v-if="type === 'zone'">
              <div class="w-full mb-2">
                <label>
                  {{ $t('LOCATIONS.FILL_FROM_STORE') }}
                  <div class="relative w-full">
                    <input
                      v-model="storeSearchQuery"
                      type="text"
                      :placeholder="$t('LOCATIONS.STORE_PLACEHOLDER')"
                      class="w-full mt-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-md p-2"
                      @input="onStoreSearchInput"
                      @focus="showStoreSuggestions = true"
                      @blur="onStoreSearchBlur"
                    />
                    <div
                      v-if="
                        showStoreSuggestions && storeSearchResults.length > 0
                      "
                      class="absolute z-[1000] w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto"
                    >
                      <div
                        v-for="store in storeSearchResults"
                        :key="store.id"
                        class="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer border-b border-slate-100 dark:border-slate-800 last:border-0"
                        @mousedown.prevent="onStoreSelect(store)"
                      >
                        <div
                          class="font-medium text-sm text-slate-800 dark:text-slate-100"
                        >
                          {{ store.name }}
                        </div>
                        <div class="text-xs text-slate-500 truncate">
                          {{ store.address || $t('LOCATIONS.NO_ADDRESS') }}
                        </div>
                      </div>
                    </div>
                    <div
                      v-else-if="showStoreSuggestions && isSearchingStores"
                      class="absolute z-[1000] w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg mt-1 p-2 text-sm text-slate-500"
                    >
                      {{ $t('LOCATIONS.SEARCHING') }}
                    </div>
                  </div>
                </label>
              </div>
            </template>

            <div v-show="formData.shape_type !== 'polygon'" class="flex gap-4">
              <div class="w-1/2">
                <label>
                  {{ $t('LOCATIONS.LATITUDE') }}
                  <input
                    v-model.number="formData.latitude"
                    type="number"
                    step="any"
                    required
                    :disabled="type === 'zone'"
                    :class="{
                      'opacity-50 cursor-not-allowed': type === 'zone',
                    }"
                  />
                </label>
              </div>
              <div class="w-1/2">
                <label>
                  {{ $t('LOCATIONS.LONGITUDE') }}
                  <input
                    v-model.number="formData.longitude"
                    type="number"
                    step="any"
                    required
                    :disabled="type === 'zone'"
                    :class="{
                      'opacity-50 cursor-not-allowed': type === 'zone',
                    }"
                  />
                </label>
              </div>
            </div>

            <div class="w-full mt-2">
              <label class="block mb-1 font-medium">{{
                $t('LOCATIONS.SHAPE')
              }}</label>
              <div class="flex gap-2">
                <button
                  type="button"
                  class="px-3 py-1 border rounded transition-colors text-sm"
                  :class="[
                    formData.shape_type === 'circle'
                      ? 'bg-slate-200 dark:bg-slate-700 border-slate-400 dark:border-slate-500 font-bold text-slate-900 dark:text-slate-100'
                      : 'bg-white dark:bg-slate-900 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300',
                  ]"
                  @click="formData.shape_type = 'circle'"
                >
                  {{ $t('LOCATIONS.CIRCLE') }}
                </button>
                <button
                  type="button"
                  class="px-3 py-1 border rounded transition-colors text-sm"
                  :class="[
                    formData.shape_type === 'sector'
                      ? 'bg-slate-200 dark:bg-slate-700 border-slate-400 dark:border-slate-500 font-bold text-slate-900 dark:text-slate-100'
                      : 'bg-white dark:bg-slate-900 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300',
                  ]"
                  @click="formData.shape_type = 'sector'"
                >
                  {{ $t('LOCATIONS.SECTOR') }}
                </button>
                <button
                  type="button"
                  class="px-3 py-1 border rounded transition-colors text-sm"
                  :class="[
                    formData.shape_type === 'polygon'
                      ? 'bg-slate-200 dark:bg-slate-700 border-slate-400 dark:border-slate-500 font-bold text-slate-900 dark:text-slate-100'
                      : 'bg-white dark:bg-slate-900 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300',
                  ]"
                  @click="formData.shape_type = 'polygon'"
                >
                  {{ $t('LOCATIONS.POLYGON') }}
                </button>
              </div>
            </div>

            <div v-show="formData.shape_type !== 'polygon'" class="w-full">
              <label>
                {{ $t('LOCATIONS.MAX_RADIUS') }}
                <input
                  v-model.number="formData.radius"
                  type="number"
                  min="100"
                  :max="maxAllowedRadius"
                />
              </label>
            </div>

            <div
              v-if="formData.shape_type === 'sector'"
              class="flex gap-4 w-full"
            >
              <div class="w-1/2">
                <label>
                  {{
                    $t('LOCATIONS.START_ANGLE', {
                      angle: formData.start_angle,
                    })
                  }}
                  <input
                    v-model.number="formData.start_angle"
                    type="range"
                    min="0"
                    max="360"
                    class="w-full"
                  />
                </label>
              </div>
              <div class="w-1/2">
                <label>
                  {{
                    $t('LOCATIONS.END_ANGLE', {
                      angle: formData.end_angle,
                    })
                  }}
                  <input
                    v-model.number="formData.end_angle"
                    type="range"
                    min="0"
                    max="360"
                    class="w-full"
                  />
                </label>
              </div>
            </div>

            <div class="w-full mt-2">
              <label class="flex items-center gap-2">
                <input v-model="formData.active" type="checkbox" />
                <span>{{ $t('LOCATIONS.ACTIVE_STATUS') }}</span>
              </label>
            </div>
          </div>

          <!-- Right side: Map -->
          <div class="w-1/2 relative min-h-[300px]">
            <div class="flex justify-between items-center mb-2">
              <label class="font-semibold text-slate-700">
                {{
                  formData.shape_type === 'polygon'
                    ? $t('LOCATIONS.DRAW_ON_MAP')
                    : $t('LOCATIONS.ADJUST_PIN')
                }}
              </label>
              <button
                v-if="formData.shape_type === 'polygon'"
                type="button"
                class="text-sm text-red-600 hover:text-red-800 font-semibold"
                @click="clearPolygon"
              >
                {{ $t('LOCATIONS.CLEAR_DRAWING') }}
              </button>
            </div>
            <div
              ref="mapContainerElement"
              class="w-full h-[350px] border border-slate-300 rounded-md bg-slate-100 dark:bg-slate-800"
            />
          </div>
        </div>

        <div class="w-full flex justify-end gap-2 items-center mt-4">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('COMMON.CANCEL')"
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="mode === 'create' ? $t('COMMON.SAVE') : $t('COMMON.UPDATE')"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<style scoped>
@import 'leaflet/dist/leaflet.css';

:deep(.modal-container) {
  width: 900px !important;
  max-width: 95vw !important;
}

.location-modal-container {
  width: 100%;
}

:deep(.leaflet-bar a) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-decoration: none !important;
  color: #333 !important;
  font-weight: bold !important;
}
:deep(.leaflet-bar a:hover) {
  background-color: #f4f4f4 !important;
}
</style>
